/* ============================================================================
   EMAIL COMPILER — Houston NFL Holdings Eloqua instance
   ----------------------------------------------------------------------------
   Pure functions. No I/O, no DOM, no React. Runs identically in the browser,
   in a Cloudflare Worker, and in CI.

   Exports
     BRANDS          brand profiles as data
     BLOCKS          the block library
     STARTERS        the starting-point templates
     compile(doc)    BlockDocument -> bulletproof HTML
     review(doc)     findings, split by who owns the fix
     preflight(html) structural guarantees, checked against output

   Conformed to
     Bulletproof Eloqua + Outlook HTML Email Build Guide  (the law)
     Texans Email Templates 01-05 + README
     Email Sending Policy & Deliverability Playbook
     Email Marketing 101

   Button pattern is guide Section 5: single real <a>, <i> spacers for Outlook,
   never a VML roundrect, never an href inside a conditional comment. Confirmed
   against all five templates and all five production seeds.
   ========================================================================== */

export const FONT = "Arial, Helvetica, sans-serif";

export const esc = (s = "") =>
  String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export const uu = (s = "") => esc(String(s).trim());

/* Deliberately tiny inline formatting. No font, size or colour control —
   brand consistency is enforced by removing the ability to break it. */
export const rich = (s = "", link = "#0080c6") =>
  esc(s)
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, `<a href="$2" target="_blank" style="color:${link}; text-decoration:underline;">$1</a>`)
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\n/g, "<br />");

/* ============================================================================
   BRAND PROFILES — data, not code.
   Adding the Foundation, Cheer or Legends is a new entry here and nothing else.
   ========================================================================== */

/* Catches "NRG Stadium", "at NRG", "NRG on Sunday" — but deliberately allows
   "Two NRG Park" and "2 NRG Park", the real postal address, which appears in
   the footer Eloqua appends. */
const NRG_RULE = { rx: /\bNRG\b(?!\s+Park)/i, label: "NRG", say: "Reliant Stadium" };

export const BRANDS = {
  texans: {
    name: "Houston Texans",
    property: "Houston Texans",
    ready: true,
    theme: "dark",
    /* OFFICIAL palette:
         Deep Steel Blue  #021018  Pantone 532 C  CMYK 92/73/29/89
         Battle Red       #ed0028  Pantone 185 C  CMYK 0/100/89/0
         Liberty White    #FFFFFF  Pantone white
         H-Town Blue      #0080c6
       The brand sheet lists Battle Red as RGB 234/0/45, which is #EA002D and
       not #ed0028. Hex governs email, so #ed0028 is used.

       DERIVED from templates 01-05, not on the official sheet. Load-bearing
       for dark emails; awaiting formal adoption:
         muted #c8ced3 · rule #1a2a36 · panel #0d2636 · page #f4f4f4          */
    c: { bg: "#021018", panel: "#0d2636", rule: "#1a2a36", text: "#ffffff", muted: "#c8ced3", primary: "#ed0028", secondary: "#0080c6", page: "#f4f4f4" },
    venue: "Reliant Stadium",
    banned: [NRG_RULE],
    senderDomain: "houstontexans.com",
    landing: "web.houstontexans.com",
    groups: [["17", "Texans - STM Insider"], ["83", "Texans - Ticket Sales & announcements"], ["64", "Texans - Priority List"], ["70", "Texans - Membership Services"], ["40", "Texans - Community Foundation"], ["63", "Texans - Gameday"]],
    footer: "Privacy Policy  |  Unsubscribe  |  Preference Center\nHouston Texans | Reliant Stadium | Two NRG Park, Houston, Texas, 77054-1573\nCopyright 2026 Houston Texans",
  },

  lsse: {
    name: "Lone Star Sports & Entertainment",
    property: "LSSE",
    ready: true,
    theme: "dark",
    /* Deep Steel Blue and Battle Red only — no H-Town Blue. Secondary actions
       use Battle Red at a smaller size rather than a second colour.
       Differs from Texans in venue, sending domain, groups and footer.
       Production seeds carry campaign art outside this palette (World Cup in
       #11174f, Kinder's Texas Bowl in #ea1f2f) — treated as per-campaign
       creative rather than brand. */
    c: { bg: "#021018", panel: "#0d2636", rule: "#1a2a36", text: "#ffffff", muted: "#c8ced3", primary: "#ed0028", secondary: "#ed0028", page: "#f4f4f4" },
    venue: "Reliant Stadium",
    banned: [NRG_RULE],
    senderDomain: "lsse.net",
    landing: "web.lsse.net",
    groups: [["32", "LSSE - Texas Bowl"], ["25", "LSSE - College Football Newsletter"], ["30", "LSSE - International Soccer Events"], ["31", "LSSE - Major Concert Events"], ["33", "LSSE - Sam Houston"], ["87", "LSSE event - KBYG"]],
    footer: "Click here to unsubscribe or to change your Subscription Preferences.\nCopyright 2026 Lone Star Sports & Entertainment | Reliant Stadium | 2 NRG Park, Houston, Texas 77054",
  },

  lovb: {
    name: "LOVB Houston",
    property: "LOVB Houston",
    ready: true,
    theme: "dark",
    /* OFFICIAL:
         Dark Blue   #051243  RGB 5/18/67    CMYK 100/96/37/49
         Light Blue  #0080ff  RGB 0/128/255  CMYK 80/48/0/0
       DERIVED by the build, NOT official — needs sign-off:
         muted #c4cde6 · rule #1c2a64 · panel #0d1c56                          */
    c: { bg: "#051243", panel: "#0d1c56", rule: "#1c2a64", text: "#ffffff", muted: "#c4cde6", primary: "#0080ff", secondary: "#4da3ff", page: "#f4f4f4" },
    venue: "",
    banned: [NRG_RULE],
    senderDomain: "",
    landing: "",
    groups: [],
    footer: "Privacy footer not yet configured for LOVB Houston.",
    incomplete: ["venue name", "sending domain", "email groups", "privacy footer language"],
  },
};

/* Image budgets. Enforced in the browser before upload; documented here so the
   Worker and CI agree with the editor. */
export const SPECS = {
  logo: { w: 400, bytes: 60 * 1024, fmt: "image/png" },
  hero: { w: 1200, bytes: 200 * 1024, fmt: "image/jpeg" },
  card: { w: 320, bytes: 90 * 1024, fmt: "image/jpeg" },
  side: { w: 480, bytes: 80 * 1024, fmt: "image/jpeg" },
};
export const IMAGE_BUDGET = 1024 * 1024;

/* ============================================================================
   BLOCK LIBRARY
   Every block traces to a template or a production seed.
   ========================================================================== */

/* ----------------------------------------------------------------------------
   FIELD MERGES
   Eloqua field merges are separate assets referenced by name, not contact field
   internal names. If a name doesn't resolve, Eloqua rejects the whole email at
   "Analyze HTML Content" with a broken Email Field tag error — before it sends.

   compile(doc, { mergeMode }) controls what gets emitted:
     "eloqua"  (default) real merge tags, for pasting into Eloqua
     "literal"            plain placeholder text, for render testing
   ---------------------------------------------------------------------------- */

let MERGE_MODE = "eloqua";

/* Verified against a real Eloqua source export. The outer span carries
   data-fieldmergename — that is the attribute Eloqua resolves. The inner span
   holds the name as visible text. Omit either and Eloqua rejects the whole
   email at "Analyze HTML Content" with a broken Email Field tag error.

   Field merge names DROP the C_ prefix that contact fields carry:
   contact field C_STM_Service_Rep_Name1 -> field merge STM_Service_Rep_Name1 */
export const fieldMerge = (name, sample) =>
  MERGE_MODE === "literal"
    ? esc(sample || name)
    : `<span class="eloquaemail" unselectable="on" spellcheck="false" autocomplete="off" autocorrect="off" autocapitalize="off" data-fieldmergename="${esc(name)}" contenteditable="false"><span contenteditable="false">${esc(name)}</span></span>`;

/* The 23 field merges that exist in the instance. Anything not on this list
   will fail Eloqua's analyse step, so the composer only offers these. */
export const FIELD_MERGES = [
  "FirstName", "LastName", "EmailAddress", "First_Name___Forms1", "Salutation_String1",
  "Mobile_Phone1", "Acct_ID_For_Link1",
  "STM_Service_Rep_Name1", "STM_Service_Rep_Title1", "STM_Service_Rep_Phone1", "STM_Service_Rep_Email1",
  "Ballroom_rep1", "STM_Terms_Accepted1", "Research___Auto_Renew1",
  "Flag___Club1", "Chairmans_club1", "Directors_club1",
  "STM_Gift_Connected_Account1", "STM_Gift___Gifting_Portal_URL1",
  "Gifting___Multiple_URLs_Club1", "Gifting___Multiple_URLs___Group_21",
  "Onetrust_NFL_Token1", "Untitled_Field_Merge14",
];

/* Rep signature sets. Only STM has a complete Name/Title/Phone/Email set in the
   instance today. Ballroom_rep1 is a name only — the other three would need
   field merges created before a Ballroom signature block can work. */
export const REP_SETS = {
  stm: { label: "STM Service Rep", name: "STM_Service_Rep_Name1", title: "STM_Service_Rep_Title1", phone: "STM_Service_Rep_Phone1", email: "STM_Service_Rep_Email1" },
  ballroom: { label: "Ballroom Rep (name only)", name: "Ballroom_rep1" },
};

let MARK = null;
let PREVIEW = false;

/* Preview only. An unfilled image slot renders as invisible dark space, which
   makes a new request look like it starts with a headline. In the editor we
   draw a dashed prompt instead. Never present in exported HTML. */
export const imgSlot = (src, alt, w, b, cls = "fluid-img", extra = "", label = "image") => {
  if (!String(src || "").trim() && PREVIEW) {
    return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;"><tbody><tr>
      <td align="center" valign="middle" style="height:${Math.max(60, Math.round(w * 0.42))}px; border:2px dashed ${b.c.rule}; background-color:${b.c.bg}; font-family:${FONT}; font-size:13px; line-height:20px; color:${b.c.muted};">
        Click to add the ${esc(label)}
      </td></tr></tbody></table>`;
  }
  return `<img src="${uu(src)}" width="${w}" alt="${esc(alt)}" style="display:block; width:100%; max-width:${w}px; border:0;${extra} font-family:${FONT}; font-size:14px; line-height:20px; color:${b.c.text}; background-color:${b.c.rule};" class="${cls}">`;
};

const row = (inner) => `
    <tr${MARK ? ` data-blk="${MARK}"` : ""}>
      ${inner}
    </tr>`;
const pad = (b) => `bgcolor="${b.c.bg}" style="padding:0 40px; background-color:${b.c.bg};"`;

/* Guide Section 5. padding v/h on the <a>; letter-spacing on both <i> equals h;
   first <i> mso-text-raise is 2v; the <span> is v. Change one, change all three
   or the Outlook button shape breaks. */
export function button(b, label, href, colour, v, h, fs, align = "center") {
  return `
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" align="${align}">
        <tbody><tr>
          <td align="center" valign="middle" bgcolor="${colour}" style="border-radius:4px; background-color:${colour};">
            <a href="${uu(href)}" target="_blank" style="display:inline-block; padding:${v}px ${h}px; font-family:${FONT}; font-size:${fs}px; font-weight:bold; color:#ffffff; text-decoration:none; text-transform:uppercase; letter-spacing:${fs >= 16 ? 2 : 1}px; border-radius:4px; background-color:${colour}; mso-padding-alt:0;">
              <!--[if mso]><i style="letter-spacing:${h}px; mso-font-width:-100%; mso-text-raise:${v * 2}px;" hidden>&nbsp;</i><![endif]-->
              <span style="mso-text-raise:${v}px;">${esc(label)}</span>
              <!--[if mso]><i style="letter-spacing:${h}px; mso-font-width:-100%;" hidden>&nbsp;</i><![endif]-->
            </a>
          </td>
        </tr></tbody>
        </table>`;
}

const image = (src, alt, w, b, cls = "fluid-img", extra = "", label = "image") =>
  imgSlot(src, alt, w, b, cls, extra, label);

export const BLOCKS = {
  "header.logo": {
    label: "Logo header", group: "Top", tiers: ["marketing", "rep"], image: "logo",
    plain: "The brand bar at the very top. Tells people instantly who this is from.",
    slots: { src: { type: "image", label: "Logo" }, alt: { type: "text", label: "Alt text", value: "" }, href: { type: "url", label: "Link", value: "" } },
    render: (s, b) => row(`<td align="center" valign="top" bgcolor="${b.c.bg}" style="padding:20px 0; background-color:${b.c.bg};">
        ${String(s.src || "").trim() || !PREVIEW
          ? `<a href="${uu(s.href)}" target="_blank" style="display:inline-block; text-decoration:none;"><img src="${uu(s.src)}" width="200" alt="${esc(s.alt)}" style="display:block; width:200px; max-width:200px; border:0;"></a>`
          : `<table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center"><tbody><tr><td align="center" valign="middle" style="width:200px; height:64px; border:2px dashed ${b.c.rule}; font-family:${FONT}; font-size:13px; color:${b.c.muted};">Click to add the logo</td></tr></tbody></table>`}
      </td>`),
  },

  "hero.image": {
    label: "Hero image", group: "Top", tiers: ["marketing"], image: "hero",
    plain: "The big picture. First thing people see — it should carry the message. Link it where the main button goes.",
    slots: {
      src: { type: "image", label: "Image" },
      alt: { type: "text", label: "Alt text", value: "", help: "Required. Outlook blocks images by default; this is what shows instead." },
      href: { type: "url", label: "Where it links", value: "", help: "Plain campaign URL with camefrom and wt.mc_id. Never anything containing elqTrackId." },
    },
    render: (s, b) => row(`<td align="center" valign="top" bgcolor="${b.c.bg}" style="padding:0; background-color:${b.c.bg};">
        <a href="${uu(s.href)}" target="_blank" style="display:block; text-decoration:none;">
          ${image(s.src, s.alt, 600, b, "fluid-img", "", "hero image")}
        </a>
      </td>`),
  },

  headline: {
    label: "Headline", group: "Words", tiers: ["marketing"],
    plain: "What is this? Be specific, not clever.",
    slots: { text: { type: "text", label: "Headline", value: "Training Camp Registration Opens June 24" } },
    render: (s, b) => row(`<td align="center" valign="top" ${pad(b)} class="mobile-pad">
        <h1 style="margin:0; font-family:${FONT}; font-size:32px; line-height:38px; font-weight:bold; color:${b.c.text}; text-align:center;" class="mobile-headline">${rich(s.text, b.c.secondary)}</h1>
      </td>`),
  },

  body: {
    label: "Body copy", group: "Words", tiers: ["marketing"],
    plain: "Why should I care? Two short paragraphs maximum. Anything longer belongs on the landing page.",
    slots: {
      p1: { type: "textarea", label: "First paragraph", value: "Registration opens Tuesday, June 24 at 10:00 AM." },
      p2: { type: "textarea", label: "Second paragraph (optional)", value: "" },
    },
    render: (s, b) => row(`<td align="center" valign="top" ${pad(b)} class="mobile-pad">
        <p style="margin:0${s.p2 ? " 0 12px" : ""}; font-family:${FONT}; font-size:16px; line-height:24px; color:${b.c.muted}; text-align:center;" class="mobile-body">${rich(s.p1, b.c.secondary)}</p>${s.p2 ? `
        <p style="margin:0; font-family:${FONT}; font-size:16px; line-height:24px; color:${b.c.muted}; text-align:center;" class="mobile-body">${rich(s.p2, b.c.secondary)}</p>` : ""}
      </td>`),
  },

  greeting: {
    label: "Greeting", group: "Words", tiers: ["rep"],
    plain: "Opens a rep letter. Eloqua fills in the first name at send time.",
    slots: {
      salutation: { type: "text", label: "Opening word", value: "Hi" },
      merge: {
        type: "select", label: "Name field", value: "FirstName",
        options: [["FirstName", "First name"], ["First_Name___Forms1", "First name (from forms)"], ["Salutation_String1", "Salutation string"]],
        help: "Salutation string is pre-formatted by Eloqua. The other two are the raw first name.",
      },
    },
    render: (s, b) => row(`<td align="left" valign="top" ${pad(b)} class="mobile-pad">
        <p style="margin:0; font-family:${FONT}; font-size:16px; line-height:26px; color:${b.c.text}; text-align:left;" class="mobile-body">${esc(s.salutation)} ${fieldMerge(s.merge || "FirstName", "Adam")},</p>
      </td>`),
  },

  letter: {
    label: "Letter copy", group: "Words", tiers: ["rep"],
    plain: "Left aligned, first person, conversational. It should read like a person typed it.",
    slots: { body: { type: "textarea", label: "Your copy", value: "I wanted to reach out personally ahead of renewals opening next month.\n\nIf there's a good time to talk it through, just reply and let me know." } },
    render: (s, b) => row(`<td align="left" valign="top" ${pad(b)} class="mobile-pad">
        <p style="margin:0; font-family:${FONT}; font-size:16px; line-height:26px; color:${b.c.muted}; text-align:left;" class="mobile-body">${rich(s.body, b.c.secondary)}</p>
      </td>`),
  },

  "cta.primary": {
    label: "Main button", group: "Action", tiers: ["marketing", "rep"],
    plain: "The one thing you want people to do. Action verbs — Buy Tickets, not Learn More.",
    slots: {
      label: { type: "text", label: "Button text", value: "BUY TICKETS" },
      href: { type: "url", label: "Where it goes", value: "", help: "Match destination to intent: ready to buy goes to checkout, browsing goes to a hub, early awareness goes to content." },
    },
    render: (s, b) => row(`<td align="center" valign="top" bgcolor="${b.c.bg}" style="padding:0 40px 20px; background-color:${b.c.bg};" class="mobile-pad mobile-cta">${button(b, s.label, s.href, b.c.primary, 16, 56, 16)}
      </td>`),
  },

  "cta.repeat": {
    label: "Repeat button", group: "Action", tiers: ["marketing"],
    plain: "A second chance for people who scrolled past the first. Same text, same destination.",
    slots: { label: { type: "text", label: "Button text", value: "BUY TICKETS" }, href: { type: "url", label: "Where it goes", value: "" } },
    render: (s, b) => row(`<td align="center" valign="top" bgcolor="${b.c.bg}" style="padding:0 40px 20px; background-color:${b.c.bg};" class="mobile-pad mobile-cta">${button(b, s.label, s.href, b.c.primary, 16, 56, 16)}
      </td>`),
  },

  "cta.secondary": {
    label: "Secondary button", group: "Action", tiers: ["marketing"],
    plain: "A lower-priority action. Only if it supports the same goal.",
    slots: { label: { type: "text", label: "Button text", value: "READ MORE" }, href: { type: "url", label: "Where it goes", value: "" } },
    render: (s, b) => row(`<td align="center" valign="top" bgcolor="${b.c.bg}" style="padding:0 40px 20px; background-color:${b.c.bg};" class="mobile-pad mobile-cta">${button(b, s.label, s.href, b.c.secondary, 12, 32, 14)}
      </td>`),
  },

  "grid.logos": {
    label: "Opponent logo grid", group: "Supporting", tiers: ["marketing"],
    plain: "Two rows of five. Cells locked at 104px because Outlook mishandles percentage widths.",
    slots: { tiles: { type: "textarea", label: "One per line: image URL | link URL | alt text", value: "" } },
    render: (s, b) => {
      const t = String(s.tiles).split("\n").filter((x) => x.trim()).map((l) => {
        const [src = "", href = "", alt = ""] = l.split("|").map((v) => v.trim());
        return { src, href, alt };
      });
      let rows = "";
      for (let i = 0; i < t.length; i += 5) {
        rows += `
            <tr>` + t.slice(i, i + 5).map((c) => `
              <td width="104" valign="middle" align="center" style="padding:8px 0;">
                <a href="${uu(c.href)}" target="_blank" style="display:block; text-decoration:none;">${image(c.src, c.alt, 104, b)}</a>
              </td>`).join("") + `
            </tr>`;
      }
      return row(`<td align="center" valign="top" ${pad(b)} class="mobile-pad">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="520" align="center" style="width:520px;"><tbody>${rows}
        </tbody></table>
      </td>`);
    },
  },

  "grid.cards": {
    label: "Three-up cards", group: "Supporting", tiers: ["marketing"],
    plain: "Three portrait cards. 160px each with 20px gutters, summing to 520px.",
    slots: { cards: { type: "textarea", label: "One per line: image URL | link URL | alt text", value: "" } },
    render: (s, b) => {
      const c = String(s.cards).split("\n").filter((x) => x.trim()).slice(0, 3).map((l) => {
        const [src = "", href = "", alt = ""] = l.split("|").map((v) => v.trim());
        return { src, href, alt };
      });
      const cells = c.map((x, i) => `${i ? `
          <td width="20" class="stack-column" style="padding:0; font-size:1px; line-height:1px;">&nbsp;</td>` : ""}
          <td width="160" valign="top" class="stack-column${i ? " stack-pad-top" : ""}" style="padding:0;">
            <a href="${uu(x.href)}" target="_blank" style="display:block; text-decoration:none;">${image(x.src, x.alt, 160, b, "fluid-img", " border-radius:4px;")}</a>
          </td>`).join("");
      return row(`<td align="center" valign="top" ${pad(b)} class="mobile-pad">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="520" align="center" style="width:520px;"><tbody><tr>${cells}
        </tr></tbody></table>
      </td>`);
    },
  },

  "story.secondary": {
    label: "Secondary story", group: "Supporting", tiers: ["marketing"], image: "side",
    plain: "A second, lower-priority message beside a photo. It should complement the main message, not compete. Stacks on a phone.",
    slots: {
      src: { type: "image", label: "Photo" }, alt: { type: "text", label: "Alt text", value: "" },
      head: { type: "text", label: "Headline", value: "Community Blitz returns" },
      body: { type: "textarea", label: "Copy — one or two sentences", value: "Volunteers head back into schools across Houston this September." },
      label2: { type: "text", label: "Button text", value: "LEARN MORE" },
      href: { type: "url", label: "Button destination — different from the main button", value: "" },
    },
    render: (s, b) => row(`<td align="center" valign="top" ${pad(b)} class="mobile-pad">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="520" align="center" style="width:520px;"><tbody><tr>
          <td width="240" valign="top" class="stack-column" style="padding:0;">${image(s.src, s.alt, 240, b, "fluid-img", " border-radius:4px;", "photo")}</td>
          <td width="24" class="stack-column" style="padding:0; font-size:1px; line-height:1px;">&nbsp;</td>
          <td width="256" valign="top" class="stack-column stack-pad-top" style="padding:0;">
            <p style="margin:0 0 8px; font-family:${FONT}; font-size:20px; line-height:26px; font-weight:bold; color:${b.c.text};">${esc(s.head)}</p>
            <p style="margin:0 0 16px; font-family:${FONT}; font-size:14px; line-height:22px; color:${b.c.muted};" class="mobile-body">${rich(s.body, b.c.secondary)}</p>${button(b, s.label2, s.href, b.c.secondary, 12, 32, 14, "left")}
          </td>
        </tr></tbody></table>
      </td>`),
  },

  "details.card": {
    label: "Details card", group: "Supporting", tiers: ["marketing", "rep"],
    plain: "A panel for the practical facts — date, time, place.",
    slots: {
      title: { type: "text", label: "Card title", value: "EVENT DETAILS" },
      lines: { type: "textarea", label: "One per line", value: "Thursday, September 3\n6:00 PM\nHouston Methodist Training Center" },
    },
    render: (s, b) => {
      const li = String(s.lines).split("\n").filter((x) => x.trim()).map((l) => `
              <tr><td style="font-family:${FONT}; font-size:15px; line-height:24px; color:${b.c.muted}; text-align:left; padding:2px 0;" class="mobile-body">${rich(l, b.c.secondary)}</td></tr>`).join("");
      return row(`<td align="center" valign="top" ${pad(b)} class="mobile-pad">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="520" align="center" bgcolor="${b.c.panel}" style="width:520px; background-color:${b.c.panel};"><tbody>
          <tr><td style="padding:20px 24px;">
            <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"><tbody>
              <tr><td style="font-family:${FONT}; font-size:12px; line-height:16px; font-weight:bold; letter-spacing:1px; color:${b.c.secondary}; text-align:left; padding-bottom:10px;">${esc(s.title)}</td></tr>${li}
            </tbody></table>
          </td></tr>
        </tbody></table>
      </td>`);
    },
  },

  "signature.card": {
    label: "Rep signature", group: "Sign off", tiers: ["rep"],
    plain: "Eloqua fills the rep's details in at send time from the contact record. Nothing renders in preview — that's expected.",
    slots: {
      set: {
        type: "select", label: "Which rep", value: "stm",
        options: Object.entries(REP_SETS).map(([k, v]) => [k, v.label]),
        help: "Only the STM set has title, phone and email field merges in the instance today.",
      },
    },
    render: (s, b) => {
      const r = REP_SETS[s.set] || REP_SETS.stm;
      const line = (n, sample) => n ? `${fieldMerge(n, sample)}<br />` : "";
      return row(`<td align="left" valign="top" ${pad(b)} class="mobile-pad">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="520" align="center" bgcolor="${b.c.panel}" style="width:520px; background-color:${b.c.panel};"><tbody>
          <tr><td style="padding:20px 24px; font-family:${FONT}; font-size:15px; line-height:23px; color:${b.c.muted}; text-align:left;">
            ${line(r.name, "Jordan Vega")}${line(r.title, "Membership Services Advisor")}${line(r.phone, "832-667-2002")}${r.email ? fieldMerge(r.email, "jvega@houstontexans.com") : ""}
          </td></tr>
        </tbody></table>
      </td>`);
    },
  },

  "eloqua.dynamic": {
    label: "Dynamic content", group: "Sign off", tiers: ["marketing", "rep"],
    plain: "A block Eloqua fills in at send time — rep signatures, sponsor panels, shared sections. Nothing renders in preview, which is expected.",
    slots: {
      elqid: {
        type: "select", label: "Which block", value: "264",
        options: [["264", "264 — STM rep signature (dark)"], ["189", "189 — General team signature"], ["1357", "1357 — Shared section"], ["1358", "1358 — Shared section"]],
        help: "The instance holds around 250 dynamic content assets. Only the ones confirmed in use are listed; ask the email team if you need another.",
      },
    },
    /* Verified against a real Eloqua export: attribute is classname, lowercase,
       and the span is empty. Eloqua injects the content at render time. */
    render: (s, b) => row(`<td align="left" valign="top" ${pad(b)} class="mobile-pad">
        <span elqid="${esc(s.elqid)}" elqtype="DynamicContent" classname="remove-absolute" style="display: block"></span>
      </td>`),
  },

  divider: {
    label: "Dividing line", group: "Layout", tiers: ["marketing", "rep"],
    plain: "Separates the main message from supporting content.",
    slots: {},
    render: (s, b) => row(`<td align="center" valign="top" ${pad(b)} class="mobile-pad">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"><tbody>
          <tr><td bgcolor="${b.c.rule}" style="height:1px; background-color:${b.c.rule}; font-size:1px; line-height:1px;">&nbsp;</td></tr>
        </tbody></table>
      </td>`),
  },

  spacer: {
    label: "Blank space", group: "Layout", tiers: ["marketing", "rep"],
    plain: "Breathing room. White space is what makes an email scannable.",
    slots: { h: { type: "text", label: "Height in pixels", value: "32" } },
    render: (s, b) => row(`<td class="mobile-spacer" bgcolor="${b.c.bg}" style="height:${parseInt(s.h, 10) || 32}px; font-size:1px; line-height:1px; background-color:${b.c.bg};">&nbsp;</td>`),
  },
};

export const GROUPS = ["Top", "Words", "Action", "Supporting", "Sign off", "Layout"];

/* ============================================================================
   COMPILER
   ========================================================================== */

/* ============================================================================
   VALIDATE — say what is wrong with a document before anything renders it
   ----------------------------------------------------------------------------
   compile() renders an unknown block as nothing. That is correct for the
   compiler (it must never throw mid-render) and dangerous for the product: a
   draft saved before a block was cut would silently lose a section. Validation
   is the loud half of that pair. Call it whenever a document arrives from
   somewhere other than the editor — a restored draft, an import, an API.
   ========================================================================== */

export function validate(doc) {
  const problems = [];
  if (!doc || typeof doc !== "object") return [{ level: "fatal", msg: "Not a document." }];
  if (!BRANDS[doc.brand]) problems.push({ level: "fatal", msg: `Unknown brand "${doc.brand}".` });
  if (!Array.isArray(doc.blocks)) problems.push({ level: "fatal", msg: "Document has no blocks." });

  (doc.blocks || []).forEach((b, i) => {
    if (!b || !b.type) {
      problems.push({ level: "drop", at: i, msg: `Block ${i + 1} has no type and will not render.` });
      return;
    }
    if (!BLOCKS[b.type]) {
      problems.push({
        level: "drop", at: i, type: b.type,
        msg: `Block ${i + 1} is a "${b.type}", which no longer exists in the library. It will not appear in the email.`,
      });
      return;
    }
    const known = Object.keys(BLOCKS[b.type].slots);
    Object.keys(b.slots || {}).forEach((k) => {
      if (!known.includes(k)) problems.push({ level: "note", at: i, msg: `Block ${i + 1} has an unused field "${k}".` });
    });
    known.forEach((k) => {
      if (b.slots?.[k] === undefined) problems.push({ level: "note", at: i, msg: `Block ${i + 1} is missing "${k}"; the default will be used.` });
    });
  });

  return problems;
}

/* Strip anything that cannot render, so a stale document still opens. Returns
   the cleaned doc and what was removed, so the UI can say so out loud. */
export function repair(doc) {
  const removed = [];
  const blocks = (doc.blocks || []).filter((b) => {
    if (b && b.type && BLOCKS[b.type]) return true;
    removed.push(b?.type || "(no type)");
    return false;
  }).map((b) => {
    const slots = {};
    Object.entries(BLOCKS[b.type].slots).forEach(([k, sl]) => { slots[k] = b.slots?.[k] ?? sl.value ?? ""; });
    return { ...b, slots };
  });
  return {
    doc: { ...doc, brand: BRANDS[doc.brand] ? doc.brand : "texans", blocks },
    removed,
  };
}

export function compile(doc, opts = {}) {
  const b = BRANDS[doc.brand];
  if (!b) throw new Error(`Unknown brand: ${doc.brand}`);

  MERGE_MODE = opts.mergeMode || "eloqua";
  PREVIEW = !!opts.markers;

  const body = doc.blocks.map((x) => {
    const d = BLOCKS[x.type];
    if (!d) return "";
    MARK = opts.markers ? x.id : null;
    const out = d.render(x.slots, b);
    MARK = null;
    return out;
  }).join("");
  MERGE_MODE = "eloqua";
  PREVIEW = false;

  /* Preview only. Shows what Eloqua will append so the requester sees the whole
     message. Never present in exported HTML. */
  const footer = opts.footer ? `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#e9edee;"><tbody><tr>
<td align="center" style="padding:18px 24px; font-family:${FONT}; font-size:11px; line-height:18px; color:#6b7a83;">
<div style="font-size:9px; letter-spacing:1px; text-transform:uppercase; color:#98a6ad; padding-bottom:8px;">Added by Eloqua at send &middot; not part of your build</div>
${esc(b.footer).replace(/\n/g, "<br />")}
</td></tr></tbody></table>` : "";

  return `<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
<html xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
<meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<meta http-equiv="X-UA-Compatible" content="IE=edge">
<meta name="x-apple-disable-message-reformatting">
<title>${esc(doc.meta.subject)}</title>

<!--[if mso]>
<noscript><xml><o:OfficeDocumentSettings>
<o:AllowPNG/><o:PixelsPerInch>96</o:PixelsPerInch>
</o:OfficeDocumentSettings></xml></noscript>
<![endif]-->

<!--[if mso]>
<style type="text/css">
body, table, td, th, p, a, span { font-family: Arial, Helvetica, sans-serif !important; }
</style>
<![endif]-->

<style type="text/css">
/* RESET */
body { margin:0 !important; padding:0 !important; -webkit-text-size-adjust:100% !important; -ms-text-size-adjust:100% !important; }
table { border-spacing:0; border-collapse:collapse; mso-table-lspace:0pt; mso-table-rspace:0pt; }
table td { border-collapse:collapse; }
img { -ms-interpolation-mode:bicubic; border:0; height:auto; line-height:100%; outline:none; text-decoration:none; }
a[x-apple-data-detectors] { color:inherit !important; text-decoration:none !important; font-size:inherit !important; font-family:inherit !important; font-weight:inherit !important; line-height:inherit !important; }

/* MOBILE */
@media only screen and (max-width:620px) {
  .email-container { width:100% !important; max-width:100% !important; }
  .fluid-img { width:100% !important; max-width:100% !important; height:auto !important; }
  .stack-column { display:block !important; width:100% !important; max-width:100% !important; }
  .stack-pad-top { padding-top:20px !important; }
  .mobile-pad { padding-left:24px !important; padding-right:24px !important; }
  .mobile-headline { font-size:28px !important; line-height:34px !important; }
  .mobile-body { font-size:15px !important; line-height:24px !important; }
  .mobile-cta { width:100% !important; }
  .mobile-cta table { width:100% !important; }
  .mobile-cta td { width:100% !important; text-align:center !important; }
  .mobile-cta a { display:block !important; width:100% !important; padding:16px 0 !important; box-sizing:border-box !important; }
  .mobile-spacer { height:24px !important; }
}
</style>
</head>

<body style="margin:0; padding:0; background-color:${b.c.page}; width:100% !important; mso-line-height-rule:exactly;">

<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" bgcolor="${b.c.page}" style="background-color:${b.c.page};">
<tbody><tr><td align="center" valign="top" style="padding:0;">

  <div style="display:none; font-size:1px; color:${b.c.page}; line-height:1px; max-height:0; max-width:0; opacity:0; overflow:hidden;">
    ${esc(doc.meta.preheader)}
  </div>

  <!--[if mso]>
  <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" align="center"><tr><td>
  <![endif]-->
  <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" align="center" class="email-container" style="margin:0 auto; max-width:600px; width:100%;">
  <tbody>${body}
  </tbody>
  </table>
  <!--[if mso]>
  </td></tr></table>
  <![endif]-->

</td></tr></tbody>
</table>${footer}
<!-- Eloqua appends the shared org footer at send time. Do NOT add a custom footer here. -->
</body>
</html>`;
}

/* ============================================================================
   REVIEW
   ========================================================================== */

const SPAM = ["act now", "free", "click here", "limited time", "urgent", "risk free", "buy now"];
const VAGUE = ["learn more", "click here", "read more", "find out more"];

export function review(doc) {
  const b = BRANDS[doc.brand];
  const yours = [], team = [];
  const y = (level, t, w, src, target) => yours.push({ level, t, w, src, target });
  const tm = (t, w) => team.push({ t, w });

  const types = doc.blocks.map((x) => x.type);
  const subj = doc.meta.subject || "", pre = doc.meta.preheader || "";
  const copyBlob = doc.blocks
    .map((x) => Object.values(x.slots || {}).filter((v) => typeof v === "string" && !v.startsWith("data:")).join(" "))
    .join(" ") + " " + subj + " " + pre;

  if (b.incomplete) y("stop", `${b.name} isn't set up yet`, `Still needs: ${b.incomplete.join(", ")}.`, "Admin");

  if (!doc.plan?.goal?.trim()) y("stop", "No goal yet", "One sentence on what should happen. If it needs two, the email is doing two jobs.", "Email Marketing 101", { field: "goal" });
  if (!doc.plan?.audience?.trim()) y("check", "No audience noted", "Who receives this changes the tone, the offer and the timing.", "Email Marketing 101", { field: "audience" });

  if (!subj.trim()) y("stop", "No subject line", "Needed before this can go anywhere.", "Eloqua", { field: "subject" });
  else if (subj.length > 50) y("stop", `Subject is ${subj.length} characters`, "Past 50 it gets cut off on phones, and that's where most people read.", "Sending Policy §12a", { field: "subject" });
  const sp = SPAM.find((w) => subj.toLowerCase().includes(w));
  if (sp) y("check", `Subject says "${sp}"`, "Known filter trigger. Say the specific thing instead.", "Sending Policy §12a");
  if (/\b[A-Z]{4,}\b/.test(subj)) y("check", "A subject word is in capitals", "Reads as shouting, to filters and to people.", "Sending Policy §12a");
  if (subj.includes("!!")) y("check", "Repeated exclamation marks", "Strong spam signal.", "Sending Policy §12a");

  if (!pre.trim()) y("stop", "No preview line", "Without it the inbox pulls your first sentence in instead.", "Build Guide §4", { field: "preheader" });
  else if (pre.length < 40 || pre.length > 100) y("check", `Preview line is ${pre.length} characters`, "Aim for 40 to 100. Extend the subject, don't repeat it.", "Build Guide §4", { field: "preheader" });

  b.banned.forEach((r) => {
    if (r.rx.test(copyBlob))
      y("stop", `This says ${r.label}`, `The venue is ${r.say} — always, no exceptions. Applies to alt text and button labels too. The only permitted use of NRG is the postal address, Two NRG Park.`, "Brand standard");
  });

  if (!doc.meta.emailGroupId) y("stop", "No email group", `Eloqua uses it to append the right ${b.property} privacy footer and handle unsubscribes.`, "Eloqua", { field: "emailGroupId" });

  doc.blocks.forEach((x) => {
    const d = BLOCKS[x.type];
    if (d?.image && !String(x.slots.src || "").trim()) y("stop", `${d.label} has no picture`, "Click the block in the preview and add one, or say in your notes what should go there.", "", { block: x.id, field: "src" });
    if (d?.slots?.alt && String(x.slots.src || "").trim() && !String(x.slots.alt || "").trim())
      y("stop", `${d.label} has no alt text`, "Outlook blocks images by default. Without this that part of the email is blank.", "Build Guide §6", { block: x.id, field: "alt" });
  });

  const hrefs = doc.blocks.flatMap((x) =>
    Object.entries(BLOCKS[x.type]?.slots || {}).filter(([, s]) => s.type === "url").map(([k]) => String(x.slots[k] || ""))
  ).filter(Boolean);

  if (hrefs.some((h) => /elqTrackId|elqTrack=/i.test(h)))
    y("stop", "A link already has tracking on it", "Paste the plain URL. Eloqua makes a fresh tracking ID at Track All; reusing an old one breaks reporting.", "Build Guide §1");
  const sh = ["bit.ly", "t.co", "tinyurl", "goo.gl"].find((s) => hrefs.some((h) => h.includes(s)));
  if (sh) y("stop", `A link uses ${sh}`, "Shorteners trip spam filters. Use the full URL.", "Sending Policy §12c");
  if (hrefs.some((h) => /^http:\/\//i.test(h))) y("check", "A link uses http", "Should be https.", "Project standard");
  if (hrefs.length && !hrefs.some((h) => /camefrom=|wt\.mc_id=/i.test(h)))
    y("check", "No campaign parameters on any link", "Add camefrom and wt.mc_id so the click can be attributed.", "Build Guide §1");

  const prim = types.filter((t) => t === "cta.primary").length;
  if (prim === 0) y("check", "No main button", "Most emails need one obvious thing to do.", "Email Marketing 101");
  if (prim > 1) y("stop", `${prim} main buttons`, "One email, one goal. Competing actions weaken all of them.", "Email Marketing 101");

  doc.blocks.filter((x) => x.type === "cta.primary" || x.type === "cta.repeat").forEach((x) => {
    if (VAGUE.includes(String(x.slots.label || "").toLowerCase().trim()))
      y("check", `Button says "${x.slots.label}"`, "Vague and low intent. Name the action — Buy Tickets, Register Now, View Schedule.", "Template README", { block: x.id, field: "label" });
  });

  /* Hierarchy: the main button must sit above supporting content. */
  const iPrim = types.indexOf("cta.primary");
  const iSupport = Math.min(...["grid.logos", "grid.cards", "story.secondary"].map((t) => {
    const i = types.indexOf(t); return i === -1 ? 999 : i;
  }));
  if (iPrim > -1 && iSupport < 999 && iPrim > iSupport)
    y("stop", "The main button is below the supporting content", "On a phone people decide before scrolling past a wall of cards. Move it above.", "Template README");

  doc.blocks.filter((x) => x.type === "body").forEach((x) => {
    [x.slots.p1, x.slots.p2].filter(Boolean).forEach((p) => {
      if (String(p).length > 220) y("check", "A paragraph is long", "One or two sentences. Detail belongs on the landing page.", "Template README");
    });
  });

  const bytes = doc.blocks.reduce((n, x) => n + (x.imageBytes || 0), 0);
  if (bytes > IMAGE_BUDGET) y("check", `Pictures total ${Math.round(bytes / 1024)} KB`, "Over 1 MB is slow on a phone. Consider dropping one.", "Project standard");
  if (!doc.blocks.length) y("stop", "Nothing here yet", "Add a block.", "");

  tm("Privacy footer", `Eloqua appends the ${b.property} footer, address and unsubscribe. Never build one.`);
  tm("Tracking links", "Applied in Eloqua with Manage Links → Track All after the HTML is pasted in.");
  tm("Rendering checks", "Email on Acid across Outlook, Gmail and Apple Mail, plus a live Outlook click test.");
  tm("Segment and send", "Audience and scheduling stay with the email team on the campaign canvas.");
  if (types.includes("signature.card")) tm("Rep details", "Pulled from the contact record at send time.");

  return { yours, team };
}

/* ============================================================================
   PREFLIGHT — structural guarantees, verified against the output itself
   ========================================================================== */

export function preflight(html) {
  const mso = html.match(/<!--\[if mso\][\s\S]*?<!\[endif\]-->/g) || [];
  return [
    ["No links inside Outlook-only comments", !mso.some((m) => /href=/i.test(m))],
    ["No VML buttons", !/<v:roundrect/i.test(html)],
    ["No tracking IDs baked into the source", !/elqTrackId|elqTrack=/i.test(html)],
    ["No custom footer", !/<a[^>]+>\s*unsubscribe/i.test(html)],
    ["Outlook 600px wrapper opened and closed", /<!--\[if mso\]>\s*<table[^>]*width="600"/.test(html) && /<\/td><\/tr><\/table>\s*<!\[endif\]-->/.test(html)],
    ["Mobile breakpoint at 620px present", /max-width:620px/.test(html)],
    ["Apple data-detector reset present", /x-apple-data-detectors/.test(html)],
    ["Every image has alt text", !/<img(?![^>]*\salt=)/i.test(html)],
    ["No fixed heights on images", !/<img[^>]*\sheight=/i.test(html)],
    ["XHTML Transitional doctype", /XHTML 1\.0 Transitional/.test(html)],
    ["Document closed properly", /<\/body>\s*<\/html>\s*$/.test(html.trim())],
  ];
}

/* ============================================================================
   STARTING POINTS
   ========================================================================== */

export const seed = (type, brand) => {
  const o = {};
  Object.entries(BLOCKS[type].slots).forEach(([k, s]) => { o[k] = s.value ?? ""; });
  if (BLOCKS[type].slots.href && !o.href) o.href = `https://www.${BRANDS[brand].senderDomain || "example.com"}/tickets?camefrom=SWAP&amp;wt.mc_id=SWAP`;
  return o;
};

let n = 0;
export const B = (type, brand, over = {}) => ({ id: `b${++n}`, type, slots: { ...seed(type, brand), ...over } });

export const STARTERS = [
  {
    id: "t06", name: "Standard email", tier: "marketing", from: "New — most emails",
    desc: "One image, one message, one action. Start here unless you need something more.",
    make: (br) => [B("header.logo", br), B("hero.image", br), B("spacer", br), B("headline", br), B("spacer", br, { h: "16" }), B("body", br), B("spacer", br), B("cta.primary", br), B("spacer", br, { h: "40" })],
  },
  {
    id: "t01", name: "Schedule / multi-matchup", tier: "marketing", from: "Template 01",
    desc: "Hero, headline, copy, button, logo grid, repeat button",
    make: (br) => [B("header.logo", br), B("hero.image", br), B("spacer", br), B("headline", br), B("spacer", br, { h: "16" }), B("body", br), B("spacer", br), B("cta.primary", br), B("spacer", br), B("divider", br), B("spacer", br), B("grid.logos", br), B("spacer", br), B("cta.repeat", br), B("spacer", br, { h: "40" })],
  },
  {
    id: "t02", name: "Featured games", tier: "marketing", from: "Template 02",
    desc: "Hero, headline, copy, button, three-up cards, repeat button",
    make: (br) => [B("header.logo", br), B("hero.image", br), B("spacer", br), B("headline", br), B("spacer", br, { h: "16" }), B("body", br), B("spacer", br), B("cta.primary", br), B("spacer", br), B("divider", br), B("spacer", br), B("grid.cards", br), B("spacer", br), B("cta.repeat", br), B("spacer", br, { h: "40" })],
  },
  {
    id: "t03", name: "Two stories", tier: "marketing", from: "Template 03",
    desc: "Main offer plus a secondary story with its own button",
    make: (br) => [B("header.logo", br), B("hero.image", br), B("spacer", br), B("headline", br), B("spacer", br, { h: "16" }), B("body", br), B("spacer", br), B("cta.primary", br), B("spacer", br), B("divider", br), B("spacer", br), B("story.secondary", br), B("spacer", br, { h: "40" })],
  },
  {
    id: "t04", name: "Letter from a rep", tier: "rep", from: "Template 04",
    desc: "Personal, left aligned, signature pulled from the contact record",
    make: (br) => [B("header.logo", br), B("spacer", br, { h: "28" }), B("greeting", br), B("spacer", br, { h: "12" }), B("letter", br), B("spacer", br), B("details.card", br), B("spacer", br), B("cta.primary", br), B("spacer", br), B("signature.card", br), B("spacer", br, { h: "40" })],
  },
  {
    id: "t05", name: "Membership confirmation", tier: "rep", from: "Template 05",
    desc: "What happened, the details, what to do next",
    make: (br) => [B("header.logo", br), B("spacer", br), B("headline", br, { text: "Your membership is confirmed" }), B("spacer", br, { h: "16" }), B("body", br, { p1: "Thanks — everything is set for the season ahead." }), B("spacer", br), B("details.card", br), B("spacer", br), B("cta.primary", br, { label: "VIEW MY ACCOUNT" }), B("spacer", br, { h: "40" })],
  },
];
